import { FloatArray, Nullable, Quaternion, Scalar, Vector3, VertexData } from '@babylonjs/core';

import { DanjiHoTransformData } from '../APIResponse';

export class Base64 {
  static decodeXZY(base64string: string) {
    if (!base64string) {
      return Array<number>();
    }

    const buffer = Buffer.from(base64string, 'base64');
    const numbers: number[] = [];

    for (let i = 0; i < buffer.length; i += 12) {
      const x = buffer.readFloatLE(i);
      const z = buffer.readFloatLE(i + 4);
      const y = buffer.readFloatLE(i + 8);
      numbers.push(x, y, z);
    }

    return numbers;
  }

  static decode(base64string: string) {
    if (!base64string) {
      return Array<number>();
    }

    const buffer = Buffer.from(base64string, 'base64');
    const numbers: number[] = [];

    for (let i = 0; i < buffer.length; i += 4) {
      numbers.push(buffer.readFloatLE(i));
    }

    return numbers;
  }
}

export function unique<T>(arr: T[], selector?: (o: T) => string): T[] {
  const set = new Set();
  const ret = arr.filter((x) => {
    const key = selector ? selector(x) : x;
    if (set.has(key)) {
      return false;
    }

    set.add(key);

    return true;
  });

  return ret;
}

export class Unit {
  static findUnitTransform(
    dataSet: DanjiHoTransformData[],
    keyData: {
      unitId: number;
      buildingName: string;
      unitName: string;
      floorIndex: number;
    }
  ) {
    if (!keyData.unitId) return undefined;
    let transform = this.getUnitTransformByIdAndFloor(dataSet, keyData.unitId, keyData.floorIndex);
    if (!transform) {
      transform = this.getUnitTransformByName(dataSet, keyData.buildingName, keyData.unitName, keyData.floorIndex);
    }
    return transform;
  }
  static getUnitTransformByIdAndFloor(dataSet: DanjiHoTransformData[], unitId: number, floorIndex: number) {
    if (!unitId) return undefined;
    return dataSet.find((data) => {
      if (!data.ho_id) return false;
      return data.ho_id === unitId && data.FloorIndex === floorIndex;
    });
  }

  static getUnitTransformByName(
    dataSet: DanjiHoTransformData[],
    buildingName: string,
    unitName: string,
    floorIndex: number
  ) {
    if (!buildingName || !unitName) return undefined;
    const lastIndex = unitName.lastIndexOf('호');
    const unitPrefix = unitName.slice(0, lastIndex) + unitName.slice(lastIndex + 1);
    const key = `${buildingName}_${unitPrefix}_${floorIndex}`;
    return dataSet.find((data) => {
      if (!data.Key) return false;
      return data.Key.startsWith(key);
    });
  }
}

export function vector3ToUniqueKey(vector: Vector3) {
  return `${vector.x},${vector.y},${vector.z}`;
}

export function distance(src: [number, number, number], dest: [number, number, number]) {
  return Vector3.Distance(new Vector3(src[0], src[1], src[2]), new Vector3(dest[0], dest[1], dest[2]));
}

export function checkApproximaterly(x: number, y: number, value?: number) {
  if (!value) {
    value = Number.EPSILON;
  }
  return Math.abs(x - y) < value;
}

export function IsNearByZero(vector1: Vector3, vector2: Vector3): boolean;
export function IsNearByZero(vector1: Vector3, vector2: Vector3, epsilon: number): boolean;
export function IsNearByZero(vector1: Vector3, vector2: Vector3, epsilon?: number) {
  return (
    Scalar.WithinEpsilon(vector1.x, vector2.x, epsilon) &&
    Scalar.WithinEpsilon(vector1.y, vector2.y, epsilon) &&
    Scalar.WithinEpsilon(vector1.z, vector2.z, epsilon)
  );
}

export function Clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function RotateAround(src: Vector3, origin: Vector3, axis: Vector3, radian: number) {
  const rotated = src.clone();

  rotated.subtractInPlace(origin);
  rotated.rotateByQuaternionToRef(Quaternion.RotationAxis(axis, radian), rotated);
  rotated.addInPlace(origin);

  return rotated;
}

export function RotateAroundToRef(src: Vector3, origin: Vector3, axis: Vector3, radian: number, result: Vector3) {
  result.copyFrom(RotateAround(src, origin, axis, radian));
}

export function LookAt(src: Vector3, target: Vector3) {
  const direction = src.subtract(target);

  direction.normalize();

  const yaw = -Math.atan2(direction.z, direction.x) - Math.PI / 2;
  const len = Math.sqrt(direction.x * direction.x + direction.z * direction.z);
  const pitch = Math.atan2(direction.y, len);

  return Quaternion.RotationYawPitchRoll(yaw, pitch, 0);
}

export function GetCenterPosition(values: Vector3[]): Vector3 {
  let sum = Vector3.Zero();
  const count = values.length;
  for (const vec of values) {
    sum = new Vector3(sum.x + vec.x, sum.y + vec.y, sum.z + vec.z);
  }

  return new Vector3(sum.x / count, sum.y / count, sum.z / count);
}

// x의 부호를 유지하며 pow 리턴
export function pow2(x: number, y: number) {
  const powed = Math.pow(Math.abs(x), y);

  return x > 0 ? powed : -powed;
}

export function findEdgeVectors(vectorArray: Vector3[]): Vector3[] {
  const edgeVectors: Vector3[] = [];
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;

  for (const vector of vectorArray) {
    minX = Math.min(minX, vector.x);
    minY = Math.min(minY, vector.y);
    minZ = Math.min(minZ, vector.z);
    maxX = Math.max(maxX, vector.x);
    maxY = Math.max(maxY, vector.y);
    maxZ = Math.max(maxZ, vector.z);
  }

  // find top, bottom, left, and rightmost Vector3s
  const topVector = vectorArray.find((vector) => vector.y === maxY);
  const bottomVector = vectorArray.find((vector) => vector.y === minY);
  const leftVector = vectorArray.find((vector) => vector.x === minX);
  const rightVector = vectorArray.find((vector) => vector.x === maxX);

  if (topVector) {
    edgeVectors.push(topVector);
  }

  if (bottomVector) {
    edgeVectors.push(bottomVector);
  }

  if (leftVector) {
    edgeVectors.push(leftVector);
  }

  if (rightVector) {
    edgeVectors.push(rightVector);
  }

  return edgeVectors;
}
export function vector3ToFloatArray(source?: Vector3[]) {
  if (!source) {
    return new Array<number>();
  }

  return source.flatMap((x) => [x.x, x.y, x.z]);
}
export function floatArrayToVector3(source?: Nullable<FloatArray>) {
  if (!source) {
    return new Array<Vector3>();
  }

  const arr = new Array<Vector3>();
  for (let i = 0; source.length > i; i += 3) {
    arr.push(new Vector3(source[i], source[i + 1], source[i + 2]));
  }
  return arr;
}

export function copyVertexData(vertexData: VertexData) {
  return getSubMesh(vertexData, 0, vertexData.positions!.length / 3);
}

export function getSubMesh(vertexData: VertexData, startIndex: number, count: number): VertexData {
  const startIdxOnFloatArr = startIndex * 3;
  const countAtFloatArr = count * 3;
  const newVertexData = new VertexData();
  if (vertexData.positions?.length)
    newVertexData.positions = vertexData.positions!.slice(startIdxOnFloatArr, startIdxOnFloatArr + countAtFloatArr);
  if (vertexData.normals?.length)
    newVertexData.normals = vertexData.normals!.slice(startIdxOnFloatArr, startIdxOnFloatArr + countAtFloatArr);
  if (vertexData.uvs?.length)
    newVertexData.uvs = vertexData.uvs!.slice(startIdxOnFloatArr, startIdxOnFloatArr + countAtFloatArr);
  if (vertexData.uvs2?.length)
    newVertexData.uvs2 = vertexData.uvs2!.slice(startIdxOnFloatArr, startIdxOnFloatArr + countAtFloatArr);
  if (vertexData.uvs3?.length)
    newVertexData.uvs3 = vertexData.uvs3!.slice(startIdxOnFloatArr, startIdxOnFloatArr + countAtFloatArr);
  if (vertexData.uvs4?.length)
    newVertexData.uvs4 = vertexData.uvs4!.slice(startIdxOnFloatArr, startIdxOnFloatArr + countAtFloatArr);
  if (vertexData.uvs5?.length)
    newVertexData.uvs5 = vertexData.uvs5!.slice(startIdxOnFloatArr, startIdxOnFloatArr + countAtFloatArr);
  if (vertexData.uvs6?.length)
    newVertexData.uvs6 = vertexData.uvs6!.slice(startIdxOnFloatArr, startIdxOnFloatArr + countAtFloatArr);
  if (vertexData.tangents?.length)
    newVertexData.tangents = vertexData.tangents!.slice(startIdxOnFloatArr, startIdxOnFloatArr + countAtFloatArr);
  if (vertexData.colors?.length)
    newVertexData.colors = vertexData.colors!.slice(startIdxOnFloatArr, startIdxOnFloatArr + countAtFloatArr);
  if (vertexData.indices?.length)
    newVertexData.indices = sortIndices(...vertexData.indices!.slice(startIndex, startIndex + count));

  return newVertexData;
}

export function sortIndices(...indices: number[]) {
  const ret = Array<number>(indices.length);
  const sorted = indices.slice().sort((x, y) => x - y);
  const map = new Map<number, number>();
  indices.forEach((x, i) => map.set(x, i));

  sorted.forEach((x, i) => {
    ret[map.get(x)!] = i;
  });

  return ret;
}

export function calculateMinMaxCoordinates(coordinatesArray: number[][]): { min: Vector3; max: Vector3 } {
  let min = new Vector3(coordinatesArray[0]![0]!, coordinatesArray[0]![1]!, coordinatesArray[0]![2]!);
  let max = new Vector3(coordinatesArray[0]![0]!, coordinatesArray[0]![1]!, coordinatesArray[0]![2]!);

  for (const coordinates of coordinatesArray) {
    for (let i = 0; i < coordinates.length; i += 3) {
      const x = coordinates[i]!;
      const y = coordinates[i + 1]!;
      const z = coordinates[i + 2]!;

      if (x < min.x) min.x = x;
      if (x > max.x) max.x = x;
      if (y < min.y) min.y = y;
      if (y > max.y) max.y = y;
      if (z < min.z) min.z = z;
      if (z > max.z) max.z = z;
    }
  }

  return { min, max };
}

export function isEmptyOrWhitespace(value: string) {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  return value.trim() === '';
}

  // 호 이름에서 숫자 부분을 추출하여 정렬하는 함수
  export const getUnitNumber = (unitName: string): number => {
    const numbers = unitName.match(/\d+/);
    return numbers ? parseInt(numbers[0], 10) : 0;
  };

  // 호 이름을 정렬하는 함수 (문자열과 숫자 혼합 지원)
  export const sortUnitNames = (unitNameA: string, unitNameB: string): number => {
    // 숫자가 없는 경우 문자열 비교
    const aHasNumber = /\d/.test(unitNameA);
    const bHasNumber = /\d/.test(unitNameB);
    
    if (!aHasNumber && !bHasNumber) {
      return unitNameA.localeCompare(unitNameB);
    }
    
    // 한쪽만 숫자가 있는 경우 숫자가 있는 쪽을 앞으로
    if (aHasNumber && !bHasNumber) return -1;
    if (!aHasNumber && bHasNumber) return 1;
    
    // 둘 다 숫자가 있는 경우 숫자로 비교
    return getUnitNumber(unitNameA) - getUnitNumber(unitNameB);
  };
